import { useEffect, useState } from "react";
import {
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Link,
  Button
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import axios from "axios";
import theme from "../../../components/LayoutComponents/theme";
import DartboardWithArrow from "../../../components/FormComponents/TargetPaper";
import LoadingCircle from "../../../components/LayoutComponents/LoadingCircle";
import ProgramCard from "./ProgramCard";
import { stateNameToAbbr } from "../../../staticDataSet";

export default function AnalyzeCard({ session, job, userComp, isSmallScreen, skills, loadingSkills, partners, programs, courses }) {
  const [schools, setSchools] = useState([]);
  const [loadingSchools, setLoadingSchools] = useState(true);
  const [showProgram, setShowProgram] = useState(false)
  const [program, setProgram] = useState(null)

  useEffect(() => {
    const fetchSchools = async () => {
      setLoadingSchools(true);
      try {
        // Convert state name to abbreviation if needed
        let state = job.state;
        if (state && state.length > 2) {
          state = stateNameToAbbr[state] || state;
        }
        const city = job.city;
        // 1. Search by city
        const apiKey = process.env.REACT_APP_COLLEGE_SCORECARD_API_KEY;
        let response = await axios.get(
          `https://api.data.gov/ed/collegescorecard/v1/schools?api_key=${apiKey}&school.city=${city}&school.state=${state}&fields=id,school.name,school.city,school.state,latest.programs.cip_4_digit.title,school.school_url`
        );
        let citySchools = response.data?.results || [];

        // Helper: does a school have a relevant program?
        const hasRelevantProgram = (school) =>
          school["latest.programs.cip_4_digit"]?.some((program) =>
            ["engineer", "technician", "manufacturing"].some((keyword) =>
              program.title.toLowerCase().includes(keyword)
            )
          );

        // 2. If no schools or none with relevant programs, search by state
        if (
          citySchools.length === 0 ||
          !citySchools.some(hasRelevantProgram)
        ) {
          response = await axios.get(
            `https://api.data.gov/ed/collegescorecard/v1/schools?api_key=${apiKey}&school.state=${state}&fields=id,school.name,school.state,latest.programs.cip_4_digit.title,school.school_url`
          );
          setSchools(response.data?.results || []);
        } else {
          setSchools(citySchools);
        }
      } catch (error) {
        setSchools([]);
      } finally {
        setLoadingSchools(false);
      }
    };
    if (job) fetchSchools();
  }, [job]);

  const handleClick = (program) => {
    setShowProgram(true)
    setProgram(program)
  }
  const handleClose = () =>{
    setShowProgram(false)
    setProgram(null)  
  }

  if (loadingSkills || loadingSchools) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <LoadingCircle />
      </Box>
    );
  }

  if (showProgram) {
    return (
      <ProgramCard session={session} program={program} handleClose={handleClose} courses={courses}  jobSkills={skills} userSkills={userComp}/>
    )
  }

  const compIdSet = new Set(userComp?.map((item) => item.competencyId));
  const unmatchedSkills = skills?.filter((item) => !compIdSet.has(item.compId));

  return (
    <Box p={2} sx={{ width: isSmallScreen ? 250 : "auto" }}>

      {!isNaN(Number(job.match)) && job.match !== "" && job.match !== null ? (
        <Typography>
          Your skills and experience give you a{" "}
          <span style={{
            color:
              Number(job.match) >= 60
                ? theme.colors.green
                : Number(job.match) >= 40
                  ? theme.colors.yellow
                  : "red"
          }}>
            {Number(job.match)}%
          </span>
        </Typography>
      ) : "There are no skills associated with this job."}

      <Box display="flex" justifyContent="center" alignItems="center" my={2}>
        {!isNaN(Number(job.match)) && job.match !== "" && job.match !== null ? (
          <DartboardWithArrow
            score={Number(job.match)}
          />
        ) : null}
      </Box>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>Missing Skills</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {(() => {
            // Group unmatchedSkills by tierName
            const tiers = {};
            unmatchedSkills.forEach((skill) => {
              const tier = skill.tierName || "Other";
              if (!tiers[tier]) tiers[tier] = [];
              tiers[tier].push(skill);
            });
            if (skills.length === 0) {
              return <Typography>No skills associated with this job.</Typography>;
            }
            if (skills.length > 0 && Object.keys(tiers).length === 0) {
              return <Typography>You matched all the skills associated with this job.</Typography>;
            }
            return Object.entries(tiers).map(([tier, skills]) => (
              <Accordion key={tier}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>{tier}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <ul>
                    {skills.map((skill, idx) => (
                      <li key={idx}>{skill.compDescription}</li>
                    ))}
                  </ul>
                </AccordionDetails>
              </Accordion>
            ));
          })()}
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>Schools nearby that will help you boost your skills.</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {/* Partner schools from props */}
          {partners && partners.length > 0 && (
            <Box mb={2}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Partner Schools
              </Typography>
              {partners.map((school) => {
                const relatedPrograms = programs?.filter(
                  (p) => p.entityId === school.entityId
                );

                return (
                  <Accordion key={`partner-${school.entityId}`}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography>{school.entityName}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography>
                        <strong>Website:</strong>{" "}
                        {school.websiteUrl ? (
                          <Link
                            href={
                              school.websiteUrl.startsWith("http")
                                ? school.websiteUrl
                                : `https://${school.websiteUrl}`
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {school.websiteUrl.replace(/\/$/, "")}
                          </Link>
                        ) : (
                          "N/A"
                        )}
                      </Typography>
                      <Typography>
                        <strong>City:</strong> {school.city}, {school.state}
                      </Typography>
                      <Typography>
                        <strong>Programs:</strong>
                      </Typography>
                      {relatedPrograms.length > 0 ? (
                        <ul>
                          {relatedPrograms
                            ?.filter((e) => e.programName && e.programName !== "")
                            .filter((prog, idx, arr) =>
                              arr.findIndex((p) => p.programName === prog.programName) === idx
                            )
                            .map((program) => (

                              <li key={program.programId}>
                                <Button
                                  onClick={() => handleClick(program)}
                                  variant="text"
                                  sx={{ color: theme.colors.purple }}
                                >
                                  {`${program.programName} `}
                                </Button>
                              </li>
                            ))}

                        </ul>
                      ) : (
                        <Typography>No programs listed</Typography>
                      )}
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>
          )}

          {/* API schools */}
          {schools && schools.length > 0 ? (
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Nearby Schools
              </Typography>
              {schools.map((school, index) => {
                const programs = school["latest.programs.cip_4_digit"]?.filter(
                  (program) =>
                    ["engineer", "technician", "manufacturing"].some((keyword) =>
                      program.title.toLowerCase().includes(keyword)
                    )
                );
                if (!programs || programs.length === 0) return null;

                const uniquePrograms = programs?.filter(
                  (prog, idx, arr) =>
                    arr.findIndex((p) => p.title === prog.title) === idx
                );

                return (
                  <Accordion key={`api-${index}`}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography>{school["school.name"]}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography>
                        <strong>Website:</strong>{" "}
                        {school.school_url ? (
                          <Link
                            href={school.school_url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {school.school_url.replace(/\/$/, "")}
                          </Link>
                        ) : (
                          "N/A"
                        )}
                      </Typography>
                      <Typography>
                        <strong>City:</strong> {school["school.city"]}
                      </Typography>
                      <Typography>
                        <strong>State:</strong> {school["school.state"]}
                      </Typography>
                      <Typography>
                        <strong>Programs:</strong>
                      </Typography>
                      <ul>
                        {uniquePrograms.map((program, idx) => (
                          <li key={idx}>{program.title}</li>
                        ))}
                      </ul>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>
          ) : (
            <Typography>No schools found in the area.</Typography>
          )}
        </AccordionDetails>
      </Accordion>

    </Box>
  );
};